import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Container,
} from '@chakra-ui/react';
import { DynamicFAQProps } from 'types/umbraco';

export default function DynamicFAQBlock({
  faqs = [],
  title,
  preamble,
}: DynamicFAQProps): JSX.Element {
  const questions = (Array.isArray(faqs) ? faqs : []).filter(
    (faq) => faq?.properties?.question && faq?.properties?.answer?.markup,
  );
  const columnSplit = Math.ceil(questions.length / 2);
  const questionColumns = [
    questions.slice(0, columnSplit),
    questions.slice(columnSplit),
  ];
  return (
    <Box p={{ base: '2em 5%', md: '3em 5%', xl: '3em 0' }}>
      <Container maxW="7xl">
        {title?.markup && (
          <Box
            mb={3}
            overflowWrap="anywhere"
            dangerouslySetInnerHTML={{ __html: title.markup }}
          />
        )}
        {preamble?.markup && (
          <Box
            mt={4}
            width={{ base: '100%', md: '75%' }}
            margin="0 auto"
            overflowWrap="anywhere"
            dangerouslySetInnerHTML={{ __html: preamble.markup }}
          />
        )}
        {questions.length > 0 && (
          <Accordion
            allowMultiple
            mt={8}
            display="grid"
            gridTemplateColumns={{
              base: 'minmax(0, 1fr)',
              md: 'repeat(2, minmax(0, 1fr))',
            }}
            columnGap={8}
            rowGap={4}
            alignItems="start">
            {questionColumns.map((column, columnIndex) => (
              <Box
                key={columnIndex}
                display="flex"
                flexDirection="column"
                gap={4}
                minW={0}>
                {column.map((faq) => (
                  <AccordionItem
                    key={faq.id}
                    borderWidth="1px"
                    borderRadius="50px"
                    sx={{
                      '&:has(button[aria-expanded="true"])': {
                        borderRadius: '20px',
                      },
                      '&:has(button:focus-visible)': {
                        outline: '2px solid',
                        outlineColor: 'black.500',
                        outlineOffset: '2px',
                      },
                    }}
                    overflow="hidden">
                    <Box as="h3" mb={0}>
                      <AccordionButton
                        px={4}
                        py={4}
                        _hover={{ bg: 'transparent' }}
                        _expanded={{ borderBottomWidth: 0 }}
                        _focus={{ boxShadow: 'none' }}
                        _focusVisible={{
                          outline: 'none',
                        }}>
                        <Box
                          flex="1"
                          minW={0}
                          textAlign="left"
                          fontWeight="bold"
                          overflowWrap="anywhere">
                          {faq.properties.question}
                        </Box>
                        <AccordionIcon flexShrink={0} ml={4} />
                      </AccordionButton>
                    </Box>
                    <AccordionPanel px={4} py={4}>
                      <Box
                        overflowWrap="anywhere"
                        dangerouslySetInnerHTML={{
                          __html: faq.properties.answer.markup,
                        }}
                      />
                    </AccordionPanel>
                  </AccordionItem>
                ))}
              </Box>
            ))}
          </Accordion>
        )}
      </Container>
    </Box>
  );
}
